/**
 * O que faz: Renderiza o gráfico de leads por dia em barras verticais (pico em degradê lima) com filtro por origem.
 * Onde usa: Dashboard principal — seção "Origem das Conversas".
 * Conexões: Recebe rawLeadsData (já filtrados pelo período) e startDate/endDate para
 *           gerar todos os dias do intervalo selecionado, mesmo sem leads.
 * Data: 11/06/2026 (barras verticais no estilo Gens em 05/10/2026).
 */

import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
  LabelList
} from 'recharts';
import { useCallback, useMemo, useState } from 'react';
import { TrendingUp } from 'lucide-react';
import { buildDailyOriginSeries } from '@/features/dashboard/report/dashboardReportAnalytics';
import type { PontoDashboard } from '@/features/dashboard/data/dashboardContracts';

interface ChartData {
  label?: string;
  leads?: number;
  category?: string;
  conversions?: number;
  [key: string]: any;
}

interface ChartCardProps {
  title: string;
  description: string;
  data: ChartData[];
  /** Mantido por compatibilidade: o gráfico agora é sempre de barras verticais. */
  type?: 'line' | 'bar';
  showFilter?: boolean;
  rawLeadsData?: any[];
  /** Série já agregada no servidor; quando presente, evita percorrer leads no navegador. */
  serieResumo?: PontoDashboard[];
  /** Informa o agrupamento aplicado pelo servidor para explicar intervalos no tooltip. */
  passoDias?: number;
  startDate?: Date | null;
  endDate?: Date | null;
}

const FILTER_OPTIONS = [
  { key: 'all', label: 'Todos' },
  { key: 'ads', label: 'Anúncios' },
  { key: 'custom_links', label: 'Links' },
  { key: 'meta', label: 'Meta Ads' },
  { key: 'google', label: 'Google Ads' }
] as const;

type FilterKey = (typeof FILTER_OPTIONS)[number]['key'];

export const ChartCard = ({
  title,
  description,
  data,
  showFilter = false,
  rawLeadsData = [],
  serieResumo,
  passoDias,
  startDate,
  endDate
}: ChartCardProps) => {
  const [filterType, setFilterType] = useState<FilterKey>('all');
  // Índice da barra sob o mouse: só troca a cor dela, sem nova consulta.
  const [barraAtiva, setBarraAtiva] = useState<number | null>(null);

  // Separa os links pelo `link_name` para o gráfico mostrar a origem rastreada,
  // inclusive quando o mesmo lead também tiver dados de anúncio.
  // Assim, o seletor reflete todos os acessos pelos links personalizados cadastrados.
  // Tabela: leads | Operação: leitura em memória | Campo: link_name | Risco: nenhum dado sensível adicional.
  const temLinkPersonalizado = (lead: any) =>
    typeof lead.link_name === 'string' && lead.link_name.trim() !== '';

  // Filtra leads por origem (Meta Ads, Google Ads, anúncios, links personalizados ou todos).
  const filterLeadsByOrigin = useCallback((leads: any[], filter: FilterKey) => {
    switch (filter) {
      case 'ads':
        // `anuncio` é o campo legado; incluí-lo mantém o filtro igual ao ranking e ao card semanal.
        return leads.filter(lead =>
          (typeof lead.ad_name === 'string' && lead.ad_name.trim() !== '') ||
          (typeof lead.anuncio === 'string' && lead.anuncio.trim() !== '')
        );
      case 'custom_links':
        return leads.filter(temLinkPersonalizado);
      case 'meta':
        return leads.filter(lead => lead.ad_platform === 'Meta Ads');
      case 'google':
        return leads.filter(lead => lead.ad_platform === 'Google Ads');
      default:
        return leads;
    }
  }, []);

  /**
   * Gera TODOS os dias do período selecionado com eachDayOfInterval.
   * Dias sem leads aparecem com 0 (não somem do gráfico).
   * Períodos acima do limite são agrupados em intervalos para cobrir toda a janela.
   */
  const chartData = useMemo(() => {
    if (!showFilter) return data;

    // O resumo já preserva as origens exclusivas e os limites de cada intervalo.
    // Quando ele existe, não lemos rawLeads para manter o dashboard leve em históricos grandes.
    if (serieResumo) {
      const campoPorFiltro: Record<FilterKey, keyof Pick<PontoDashboard, 'todos' | 'anuncios' | 'links_rastreados' | 'meta' | 'google'>> = {
        all: 'todos', ads: 'anuncios', custom_links: 'links_rastreados', meta: 'meta', google: 'google',
      };
      const campo = campoPorFiltro[filterType];
      return serieResumo.map((item) => ({
        label: item.inicio === item.fim ? item.inicio : `${item.inicio}–${item.fim}`,
        leads: item[campo], inicio: item.inicio, fim: item.fim, isInterval: item.inicio !== item.fim,
      }));
    }

    // Compatibilidade: consumidores legados ainda podem passar leads até concluírem a migração.
    const filteredLeads = filterLeadsByOrigin(rawLeadsData, filterType);

    // Reutiliza o agrupamento do relatório para manter os valores iguais entre a tela e o PDF.
    return buildDailyOriginSeries(filteredLeads, startDate ?? null, endDate ?? null, 90)
      .map((item) => ({ label: item.label, leads: item.total, isInterval: item.isInterval }));
  }, [showFilter, data, rawLeadsData, serieResumo, filterType, startDate, endDate, filterLeadsByOrigin]);

  const currentFilter = FILTER_OPTIONS.find(f => f.key === filterType);
  const totalLeads = chartData.reduce((sum, item) => sum + (item.leads || 0), 0);
  const peakDay = chartData.reduce(
    (max, item) => ((item.leads || 0) > (max?.leads || 0) ? item : max),
    null as ChartData | null
  );

  const formatarDataDica = (data: string) => {
    const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(data);
    return partes ? `${partes[3]}/${partes[2]}/${partes[1]}` : data;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const ponto = payload[0]?.payload as ChartData & { inicio?: string; fim?: string };
      return (
        <div className="rounded-xl border border-border bg-card px-3.5 py-2.5 shadow-lg">
          <p className="text-xs font-medium text-muted-foreground">{ponto.inicio && ponto.fim ? ponto.inicio === ponto.fim ? formatarDataDica(ponto.inicio) : `${formatarDataDica(ponto.inicio)} até ${formatarDataDica(ponto.fim)}` : label}</p>
          <p className="mt-0.5 text-sm font-semibold tabular-nums text-foreground">
            {payload[0].value} {payload[0].value === 1 ? 'lead' : 'leads'}
          </p>
          {showFilter && filterType !== 'all' && (
            <p className="mt-0.5 text-[11px] text-muted-foreground">{currentFilter?.label}</p>
          )}
          {ponto.inicio && ponto.inicio !== ponto.fim && passoDias && (
            <p className="mt-0.5 text-[11px] text-muted-foreground">Agrupado em blocos de até {passoDias} dias</p>
          )}
        </div>
      );
    }
    return null;
  };

  // Barras verticais inspiradas na "prova numérica" da skill gens-elevenlabs-criativos.
  // Todas as barras usam o mesmo degradê lima vivo (o gráfico compara todos os dias, sem barra apagada);
  // a barra sob o mouse ganha um degradê mais luminoso e o halo, a pedido do usuário em 05/10/2026.
  const quantidadeBarras = chartData.length;
  // Poucas barras recebem o valor no topo; com muitas, o número aparece só na barra sob o mouse e na dica.
  const mostrarValores = quantidadeBarras <= 14;
  // Raio e espaço proporcionais: 90 dias com raio grande viram "pílulas" ilegíveis.
  const raioBarra = quantidadeBarras > 45 ? 3 : quantidadeBarras > 20 ? 6 : 10;
  const chartMargins = { top: 22, right: 4, left: -16, bottom: 0 };
  const axisTickStyle = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' };

  // Datas ISO viram dd/mm no eixo; intervalos mostram o primeiro dia do bloco.
  const formatarRotuloEixo = (rotulo: string) => {
    const data = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(rotulo ?? ''));
    return data ? `${data[3]}/${data[2]}` : String(rotulo ?? '');
  };

  const renderChart = () => (
    // A key troca com o filtro: as barras entram com fade (opacidade), sem animar altura pelo recharts.
    <div key={filterType} className="h-full w-full animate-in fade-in-0 duration-300 motion-reduce:animate-none">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartData}
          margin={chartMargins}
          barCategoryGap={quantidadeBarras > 30 ? '14%' : '26%'}
          onMouseMove={(estado) => setBarraAtiva(typeof estado?.activeTooltipIndex === 'number' ? estado.activeTooltipIndex : null)}
          onMouseLeave={() => setBarraAtiva(null)}
        >
          <defs>
            {/* Barra comum: luz lima no topo, verde-oliva na base (paleta gens-lima), igual para todos os dias. */}
            <linearGradient id="barraComumGens" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#d8ff3c" />
              <stop offset="55%" stopColor="#bada55" />
              <stop offset="100%" stopColor="#5c8a1d" />
            </linearGradient>
            {/* Barra em destaque (mouse): o mesmo degradê mais luminoso, para a barra "acender". */}
            <linearGradient id="barraDestaqueGens" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#ecff8a" />
              <stop offset="45%" stopColor="#d8ff3c" />
              <stop offset="100%" stopColor="#7fa53a" />
            </linearGradient>
            {/* Brilho suave em volta da barra em destaque, como o halo do criativo de referência. */}
            <filter id="brilhoBarraGens" x="-50%" y="-20%" width="200%" height="140%">
              <feDropShadow dx="0" dy="0" stdDeviation="5" floodColor="#bada55" floodOpacity="0.55" />
            </filter>
          </defs>
          <CartesianGrid strokeDasharray="3 5" stroke="hsl(var(--border))" vertical={false} />
          <XAxis
            dataKey="label"
            tickFormatter={formatarRotuloEixo}
            tick={axisTickStyle}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
            minTickGap={18}
            tickMargin={8}
          />
          <YAxis tick={axisTickStyle} axisLine={false} tickLine={false} allowDecimals={false} width={40} />
          <Tooltip content={<CustomTooltip />} cursor={false} />
          <Bar dataKey="leads" radius={[raioBarra, raioBarra, Math.min(4, raioBarra), Math.min(4, raioBarra)]} maxBarSize={56} isAnimationActive={false}>
            {chartData.map((ponto, indice) => (
              <Cell
                key={`${ponto.label}-${indice}`}
                // Todas iguais e vivas; a barra sob o mouse acende (degradê mais claro + halo).
                fill={indice === barraAtiva ? 'url(#barraDestaqueGens)' : 'url(#barraComumGens)'}
                filter={indice === barraAtiva ? 'url(#brilhoBarraGens)' : undefined}
              />
            ))}
            <LabelList
              dataKey="leads"
              content={({ x, y, width, value, index }: { x?: number | string; y?: number | string; width?: number | string; value?: number | string; index?: number }) => {
                const emDestaque = index === barraAtiva;
                if (!value || (!mostrarValores && !emDestaque)) return null;
                return (
                  <text
                    x={Number(x) + Number(width) / 2}
                    y={Number(y) - 7}
                    textAnchor="middle"
                    className="tabular-nums"
                    style={{ fontSize: emDestaque ? 12 : 11, fontWeight: emDestaque ? 700 : 600, fill: 'hsl(var(--foreground))', fontFamily: "'Sora', 'DM Sans', system-ui, sans-serif" }}
                  >
                    {value}
                  </text>
                );
              }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );

  const hasData = chartData && chartData.length > 0 && totalLeads > 0;

  return (
    <div className="flex flex-col rounded-2xl border border-border bg-card p-4 shadow-[0_1px_2px_rgba(19,41,27,0.05),0_3px_10px_rgba(19,41,27,0.05)] sm:p-6">
      {/* Header: título + filtros segmentados */}
      <div className="mb-4 sm:mb-5">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h3 className="text-[15px] font-semibold text-foreground">{title}</h3>
            <p className="mt-0.5 text-[13px] text-muted-foreground">{description}</p>
          </div>

          {showFilter && (
            <div
              role="group"
              aria-label="Filtrar origem das conversas"
              className="flex max-w-full shrink-0 items-center gap-0.5 self-start overflow-x-auto rounded-full bg-secondary p-1"
            >
              {FILTER_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setFilterType(option.key)}
                  aria-pressed={filterType === option.key}
                  className={`min-h-9 cursor-pointer whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-semibold transition-[background-color,color,box-shadow] duration-200 motion-reduce:transition-none ${
                    filterType === option.key
                      ? 'bg-card text-foreground shadow-[0_1px_2px_rgba(19,41,27,0.08),0_3px_8px_rgba(19,41,27,0.06)]'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Resumo do período */}
        {hasData && (
          <div className="mt-3 flex items-baseline gap-4">
            <div className="flex items-baseline gap-1.5">
              <span className="lg-display text-[1.75rem] font-bold leading-none tabular-nums tracking-tight text-foreground">{totalLeads}</span>
              <span className="text-xs text-muted-foreground">leads no período</span>
            </div>
            {peakDay && (peakDay.leads || 0) > 0 && chartData.length > 1 && (
              <span className="hidden sm:inline text-xs text-muted-foreground">
                {peakDay.isInterval ? 'Maior volume:' : 'Pico:'} <span className="font-medium text-foreground">{formatarRotuloEixo(String(peakDay.label))}</span> ({peakDay.leads})
              </span>
            )}
          </div>
        )}
        {serieResumo && passoDias > 1 && (
          <p className="mt-2 text-xs text-muted-foreground">
            Cada barra mostra o intervalo de início e fim; a série usa blocos de até {passoDias} dias.
          </p>
        )}
      </div>

      {/* Gráfico */}
      {/* No desktop o gráfico preenche a altura do card: o card ao lado (anúncios) pode deixar a linha
          da grade mais alta, e sem isso sobraria um vão em branco embaixo do gráfico. */}
      <div className="h-56 w-full sm:h-64 lg:h-auto lg:min-h-64 lg:flex-1">
        {hasData ? (
          renderChart()
        ) : (
          <div className="flex h-full items-center justify-center">
            <div className="text-center">
              <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-muted">
                <TrendingUp className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
              </div>
              <p className="text-sm text-muted-foreground">
                {showFilter && filterType !== 'all'
                  ? `Nenhum lead de ${currentFilter?.label} no período`
                  : 'Nenhum dado no período selecionado'}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
